/**
 * ImageNode - Displays uploaded images on the canvas
 *
 * Features:
 * - Displays image from R2 URL (via proxy)
 * - NodeResizer for resize handles
 * - Maintains aspect ratio on resize (keepAspectRatio)
 * - Connection handles on all sides
 * - Loading state while image loads
 * - Error state if image fails to load
 *
 * How it works:
 * 1. User uploads image via NodeToolbar
 * 2. Image is stored in R2 via /api/upload/image endpoint
 * 3. Proxy URL is returned (e.g., /api/upload/image/abc123.png)
 * 4. ImageNode displays using that URL
 *
 * Data structure stored in Y.Map:
 * {
 *   src: '/api/upload/image/abc123.png',
 *   alt: 'original-filename.png',
 *   width: 300,
 *   height: 200,
 *   borderRadius: 4,
 *   caption: 'Optional caption'
 * }
 */
import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';

// ============================================================================
// NODE DATA TYPES
// ============================================================================

/**
 * ImageData - Data shape stored in Y.Map for image nodes
 *
 * @property src - R2 public URL or proxy URL for the image
 * @property alt - Alt text for accessibility
 * @property originalWidth - Original image dimensions (for aspect ratio)
 * @property originalHeight - Original image dimensions (for aspect ratio)
 * @property width - Current display width
 * @property height - Current display height
 * @property caption - Optional caption text below image
 * @property borderRadius - Border radius (0 for sharp, 8 for rounded, etc.)
 *
 * Note: Index signature required for React Flow Node<T> type compatibility
 */
export interface ImageData {
  /** R2 public URL or proxy URL for the image */
  src: string;

  /** Alt text for accessibility */
  alt: string;

  /** Original image dimensions (for aspect ratio calculation) */
  originalWidth?: number;
  originalHeight?: number;

  /** Current display dimensions */
  width?: number;
  height?: number;

  /** Optional caption displayed below image */
  caption?: string;

  /** Border radius (0 for none, 8 for rounded, etc.) */
  borderRadius?: number;

  /** Index signature for React Flow compatibility */
  [key: string]: unknown;
}

/**
 * ImageNodeType - Full node type for type-safe React Flow usage
 *
 * Use this type when working with image nodes in React Flow:
 * - In nodeTypes registry
 * - When creating new image nodes
 * - In onNodesChange handlers
 */
export type ImageNodeType = Node<ImageData, 'image'>;

// ============================================================================
// IMAGE NODE COMPONENT
// ============================================================================

/**
 * ImageNode - The actual image component
 *
 * State management:
 * - isLoading: true while image is loading
 * - hasError: true if image failed to load
 *
 * Resize behavior:
 * - keepAspectRatio={true} maintains proportions
 * - User drags corner handles to resize
 */
export const ImageNode = memo(({ id, data, selected }: NodeProps<ImageNodeType>) => {
  // ---------------------------------------------------------------------------
  // LOCAL STATE FOR LOADING/ERROR
  // ---------------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // ---------------------------------------------------------------------------
  // EXTRACT DATA WITH DEFAULTS
  // ---------------------------------------------------------------------------
  const {
    src,
    alt = 'Uploaded image',
    width = 200,
    height = 150,
    borderRadius = 4,
    caption,
  } = data;

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------

  /**
   * Handle successful image load
   * Clears loading and error states
   */
  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  /**
   * Handle image load error
   * Shows error state instead of broken image
   */
  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  /**
   * Reset loading state when src changes
   * This happens when a different image is selected or updated
   */
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* ===================================================================
          RESIZE HANDLES
          NodeResizer provides drag-to-resize functionality.
          keepAspectRatio maintains image proportions during resize.
          Only visible when the node is selected.
          =================================================================== */}
      <NodeResizer
        minWidth={50}
        minHeight={50}
        isVisible={selected}
        lineClassName="border-blue-500"
        handleClassName="bg-blue-500 border-2 border-white rounded"
        keepAspectRatio={true}
      />

      {/* ===================================================================
          CONNECTION HANDLES
          Handles on all 4 sides allow flexible edge connections.
          - Top/Left: target (incoming connections)
          - Bottom/Right: source (outgoing connections)
          =================================================================== */}
      <Handle type="target" position={Position.Top} id="top" className="w-3 h-3" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="w-3 h-3" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3" />

      {/* ===================================================================
          IMAGE CONTAINER
          Holds the image with loading/error states.
          Border changes when selected.
          =================================================================== */}
      <div
        className="relative bg-gray-100 flex items-center justify-center overflow-hidden shadow-md"
        style={{
          width,
          height,
          borderRadius,
          border: selected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
        }}
      >
        {/* -----------------------------------------------------------------
            LOADING STATE
            Shows spinner while image is loading.
            Only visible when isLoading is true and no error.
            ----------------------------------------------------------------- */}
        {isLoading && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <svg className="animate-spin h-8 w-8 text-gray-400" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
        )}

        {/* -----------------------------------------------------------------
            ERROR STATE
            Shows warning icon if image failed to load.
            Common causes: 404, CORS issues, invalid URL.
            ----------------------------------------------------------------- */}
        {hasError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 text-gray-400">
            <svg className="h-8 w-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-xs">Failed to load</span>
          </div>
        )}

        {/* -----------------------------------------------------------------
            IMAGE ELEMENT
            Hidden while loading or if error.
            object-contain ensures image fits without cropping.
            draggable={false} prevents browser drag behavior.
            ----------------------------------------------------------------- */}
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`max-w-full max-h-full object-contain ${isLoading || hasError ? 'invisible' : ''}`}
          draggable={false}
        />
      </div>

      {/* ===================================================================
          CAPTION (optional)
          Displayed below the image if provided.
          Truncated with ellipsis if too long.
          =================================================================== */}
      {caption && (
        <div
          className="absolute -bottom-6 left-0 right-0 text-center text-xs text-gray-500 truncate px-2"
          style={{ width }}
        >
          {caption}
        </div>
      )}
    </>
  );
});

// Display name for React DevTools debugging
ImageNode.displayName = 'ImageNode';
